import React, { useState, useEffect } from "react";
import { TextField, Button, Typography, RadioGroup, CircularProgress, Box, FormControlLabel, Radio, Grid, FormLabel } from '@mui/material';
import { addStaff } from 'api/company';
import { StaffSchema } from 'models/StaffSchema';
import { useParams } from 'react-router-dom';
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'components/snackbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCompanyStaffDetails, updateCompanyStaff } from 'api/company';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

/**
 * Component to handle the form to add or edit a staff member for a company.
 * If the url contains 'add-staff', it will render a form to add a new staff member.
 * Otherwise, it will render a form to edit a staff member.
 * @returns {React.ReactElement} The JSX element representing the form to add or edit a staff member.
 */
const CompanyStaffForm = () => {
  const { id } = useParams();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [companyStaffDetails, setCompanyStaffDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const containsExample = location.pathname.includes('add-staff');
  const isEditMode = !containsExample;
  const { companyId } = location.state || {};


  /**
   * Handles navigation to the company details page after a staff member is added or edited.
   * @param {number} companyId - The id of the company to navigate to.
   */
  const handleNavigation = (companyId) => {
    navigate(`/company-details/${companyId}`);
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(StaffSchema),
    defaultValues: {
      name: '',
      business_function: '',
      designation: '',
      email: '',
      phone_number: '',
      date_of_joining: '',
      is_tj_source: false,
    },
  });
  useEffect(() => {
    /**
     * Fetches the company staff details given the id and sets the form values to the fetched data.
     * If the company response is successful and the data is not null, it sets the company staff details and
     * displays a success message to the snackbar.
     * If the company response is not successful or the data is null, it displays an error message to the snackbar.
     * @throws {Error} if unable to fetch the company staff data
     */
    const fetchData = async () => {
      try {
        if (!isEditMode) {
          setLoading(false);
          return;
        }
        const response = await getCompanyStaffDetails({ companyId, staffId: id });
        if (response?.success && response.data != null) {
          setCompanyStaffDetails(response.data);
          enqueueSnackbar(response.message, { variant: 'success' });
        } else {
          enqueueSnackbar('Failed to fetch company data. Please try again later.', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        enqueueSnackbar('An unexpected error occurred while fetching company data.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    if (isEditMode && companyStaffDetails) {
      Object.keys(companyStaffDetails).forEach(key => {
        methods.setValue(key, companyStaffDetails[key]);
      });
    }
  }, [isEditMode, companyStaffDetails, methods]);


  const { handleSubmit, formState: { errors, isSubmitting }, setValue } = methods;
  const handleStartDateChange = date => {
    setValue('date_of_joining', date.$d);
  }
  /**
   * Submits the company staff form data.
   * 
   * If in edit mode, it updates the existing company staff details.
   * If not in edit mode, it adds a new staff member.
   * Displays success or error messages based on the response.
   * Handles navigation upon successful submission.
   * Logs errors and displays a snackbar with error messages when exceptions occur.
   * 
   * @param {Object} data - The form data to be submitted.
   * @returns {Promise<void>}
   */

  const onSubmit = async (data) => {
    try {
      let response;
      if (isEditMode) {
        if (!companyStaffDetails) {
          enqueueSnackbar('Company staff details not available. Please try again later.', { variant: 'error' });
          return;
        }
        response = await updateCompanyStaff({ companyId, staffId: id, updates: data });
        if (response?.success && response.data != null) {
          enqueueSnackbar(response.message, { variant: 'success' });
          handleNavigation(companyId);
        } else {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
      } else {
        response = await addStaff({ id, ...data });
        if (response?.success === true && response.data) {
          enqueueSnackbar(response.message, { variant: 'success' });
          navigate('/companies');
        } else {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
      }

    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar('An unexpected error occurred. Please try again later.', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box sx={{ maxWidth: '900px', marginTop: '20px', marginLeft: 'auto', marginRight: 'auto', marginTop: '40px' }}>
        <Typography variant="h5" align="center" gutterBottom>
          Staff Details
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            label="Name"
            InputLabelProps={{ shrink: true }}
            {...methods.register('name')}
            fullWidth
            margin="normal"
            error={!!errors.name}
            helperText={errors.name?.message}
          />
          <TextField
            label="Business Function"
            InputLabelProps={{ shrink: true }}
            {...methods.register('business_function')}
            fullWidth
            margin="normal"
            error={!!errors.business_function}
            helperText={errors.business_function?.message}
          />
          <TextField
            label="Designation"
            InputLabelProps={{ shrink: true }}
            {...methods.register('designation')}
            fullWidth
            margin="normal"
            error={!!errors.designation}
            helperText={errors.designation?.message}
          />
          <TextField
            label="Email"
            {...methods.register('email')}
            fullWidth
            InputLabelProps={{ shrink: true }}
            margin="normal"
            error={!!errors.email}
            helperText={errors.email?.message}
          />
          <TextField
            label="Phone Number"
            {...methods.register('phone_number')}
            fullWidth
            type='number'
            InputLabelProps={{ shrink: true }}
            margin="normal"
            error={!!errors.phone_number}
            helperText={errors.phone_number?.message}
          />
          <Controller
            name="date_of_joining"
            control={methods.control}
            render={({ field }) => (
              <DatePicker
                {...field}
                value={dayjs(field?.value)}
                autoOk={true}
                label='Joining Date'
                closeOnSelect={true}
                format='DD/MM/YYYY'
                disableFuture
                InputLabelProps={{ shrink: true }}
                error={!!errors.date_of_joining}
                helperText={errors.date_of_joining?.message}
                onChange={(date) => handleStartDateChange(date)}
              />
            )}
          />
          <Grid item xs={12}>
            <FormLabel>TJ Sourced?</FormLabel>
            <Controller
              name="is_tj_source"
              control={methods.control}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  row
                  value={field.value ?? false}
                  onChange={(e) => field.onChange(e.target.value === "true")}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              )}
            />
          </Grid>

          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting} fullWidth>
            {isSubmitting ? "Submitting.." : (isEditMode ? "Update" : "Save")}
          </Button>
        </form>
      </Box>
    </FormProvider>
  );
};

export default CompanyStaffForm;
